import { DEFAULT_CURRENCY_CODE } from "@/utils/numberFormat";
import Text from "@/components/ui/text";
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ContractBadge } from "@dreamhub/designsystem/icons";
import { LICENSE_TYPE_ID, useProducts } from "@/entities/settings/hooks";
import { ServiceItem } from "@/pages/settings/pages/products-v2/types";
import { SECTIONS } from "@/pages/settings/pages/products-v2/consts";
import { useServiceItemTypes } from "@/pages/settings/pages/products-v2/hooks";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { useCreateContract } from "@/pages/contracts/hooks";
import { DealData } from "@/pages/deals/types";
import AddServiceDrawer from "../AddServiceDrawer";
import AddServiceDropdown from "./AddServiceDropdown";
import ProductEmptyState from "./ProductEmptyState";
import ServiceEmptyState from "./ServiceEmptyState";
import { hasConfiguredPricing } from "../utils";
import { useAddBundle } from "../hooks/useAddBundle";

interface Props {
  productId?: string;
  contractId?: string;
  // Nullable and undefaulted: this is forwarded to AddServiceDrawer, whose
  // summaries hand it to useCurrencyConversion. Only the createContract
  // payload below defaults it, because there the value becomes the
  // contract's currency for real.
  currency?: string | null;
  dealId?: string;
  companyId?: string;
  onDealUpdate?: (updatedFields: Partial<DealData>) => void;
  disabled?: boolean;
  disabledTooltipText?: string;
  // Settles a failed create that may still have landed; without it the failure is toasted.
  onCreateFailed?: (service: ServiceItem) => Promise<void> | void;
  // A recovered pick to open the drawer on once its contract is known.
  pendingService?: ServiceItem | null;
  onPendingServiceConsumed?: () => void;
  hideAddService?: boolean;
  // The deal's Terms & Services also offers the product's bundles.
  offerBundles?: boolean;
}

const EmptyState: React.FC<Props> = ({
  productId,
  contractId,
  currency,
  dealId,
  companyId,
  onDealUpdate,
  disabled = false,
  disabledTooltipText,
  onCreateFailed,
  pendingService,
  onPendingServiceConsumed,
  hideAddService = false,
  offerBundles = false
}) => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(
    null
  );
  const [localContractId, setLocalContractId] = useState<string | undefined>(
    contractId
  );
  const createContract = useCreateContract();
  const createContractRef = useRef(createContract);
  createContractRef.current = createContract;
  // Set synchronously: isPending lags a render, so two quick picks could both create.
  const createInFlightRef = useRef(false);
  // Locks the trigger while a failed create is settled.
  const [isRecovering, setIsRecovering] = useState(false);
  // Read by async mutation callbacks so a create that was already in flight
  // when the lock landed can't reopen the drawer.
  const disabledRef = useRef(disabled);
  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);
  const { data: productsData } = useProducts();

  useEffect(() => {
    setLocalContractId(contractId);
  }, [dealId, contractId]);

  useEffect(() => {
    if (disabled) {
      setIsDrawerOpen(false);
      setSelectedService(null);
    }
  }, [disabled]);

  useEffect(() => {
    if (!pendingService) return;
    // A lock discards the pick so a later unlock can't open a stale drawer.
    if (disabled) {
      onPendingServiceConsumed?.();
      return;
    }
    if (!localContractId) return;
    setSelectedService(pendingService);
    setIsDrawerOpen(true);
    onPendingServiceConsumed?.();
  }, [pendingService, localContractId, disabled, onPendingServiceConsumed]);
  const { data: serviceItemTypes = [] } = useServiceItemTypes();

  const selectedProduct = useMemo(() => {
    if (!productId || !productsData?.products) return null;
    return productsData.products.find((p: any) => p.id === productId) || null;
  }, [productId, productsData]);

  const availableServices = useMemo(() => {
    if (!selectedProduct) return [];
    return (selectedProduct?.serviceItems ?? [])
      .map((item: ServiceItem) => ({
        ...item,
        productId: item.productId || productId
      }))
      .filter(hasConfiguredPricing) as ServiceItem[];
  }, [productId, selectedProduct]);

  const {
    bundles,
    onSelectBundle,
    drawer: bundleDrawer
  } = useAddBundle({
    offerBundles,
    productId,
    contractId: localContractId,
    dealId,
    services: availableServices,
    serviceItemTypes,
    currency,
    disabled,
    onAdded: (bundle) => {
      setLocalContractId(bundle.contractId);
      onDealUpdate?.({ contractId: bundle.contractId });
    }
  });

  const handleServiceSelect = useCallback(
    (service: ServiceItem) => {
      if (disabled) return;
      setSelectedService(service);
      if (localContractId) {
        setIsDrawerOpen(true);
        return;
      }
      // A held recovered pick means its contract is on its way.
      if (createInFlightRef.current || pendingService) return;
      createInFlightRef.current = true;
      createContractRef.current.mutate(
        {
          name: service.name,
          dealId,
          companyId,
          productId,
          // `||` not `??`: an empty string is as unset as null here, and it
          // would otherwise be written as the new contract's currency.
          currency: currency || DEFAULT_CURRENCY_CODE
        },
        {
          onSuccess: (response) => {
            createInFlightRef.current = false;
            const newContractId = response?.id;
            if (!newContractId) {
              toast.error(t("deals.commercials.updateError"));
              return;
            }
            setLocalContractId(newContractId);
            if (disabledRef.current) return;
            setIsDrawerOpen(true);
          },
          onError: () => {
            if (!onCreateFailed) {
              createInFlightRef.current = false;
              toast.error(t("deals.commercials.updateError"));
              return;
            }
            setIsRecovering(true);
            Promise.resolve()
              .then(() => onCreateFailed(service))
              .catch(() => toast.error(t("deals.commercials.updateError")))
              .finally(() => {
                createInFlightRef.current = false;
                setIsRecovering(false);
              });
          }
        }
      );
    },
    [
      localContractId,
      dealId,
      companyId,
      productId,
      currency,
      t,
      disabled,
      onCreateFailed,
      pendingService
    ]
  );

  const handleAddToContract = () => {
    if (disabled) return;
    if (localContractId) {
      onDealUpdate?.({ contractId: localContractId });
    }
    setIsDrawerOpen(false);
    setSelectedService(null);
  };

  const handleCloseDrawer = () => {
    if (localContractId) {
      onDealUpdate?.({ contractId: localContractId });
    }
    setIsDrawerOpen(false);
    setSelectedService(null);
  };

  const serviceOptions: DetailedViewOptions = useMemo(() => {
    const options: DetailedViewOptions = {};

    availableServices.forEach((service) => {
      const serviceType = serviceItemTypes.find(
        (type) => type.id === service.typeId
      );
      options[service.id] = {
        action: () => handleServiceSelect(service),
        customText: service.name,
        section: service.typeId === LICENSE_TYPE_ID ? "1" : "2",
        secondaryText: serviceType?.name || String(service.typeId)
      };
    });

    return options;
  }, [availableServices, serviceItemTypes, handleServiceSelect]);

  const sections = SECTIONS.map((section) => ({
    id: section.id.toString(),
    title: t(section.name)
  }));

  const emptyStateContent = useMemo(() => {
    if (!productId) {
      return <ProductEmptyState />;
    }
    if (availableServices.length === 0) {
      return <ServiceEmptyState />;
    }
    return undefined;
  }, [productId, availableServices.length]);

  return (
    <>
      <div className="flex flex-col items-center justify-center h-100 rounded-lg p-6 gap-6">
        <ContractBadge />
        <div className="flex flex-col items-center justify-center">
          <Text variant="body" className="mb-2">
            {t("contractTerms.noServicesYet")}
          </Text>
          <Text variant="bodySecondary">
            {t("contractTerms.noServicesDescription")}
          </Text>
        </div>

        {hideAddService ? null : (
          <AddServiceDropdown
            serviceOptions={serviceOptions}
            sections={sections}
            variant="primary"
            emptyStateContent={emptyStateContent}
            disabled={disabled}
            disabledTooltipText={disabledTooltipText}
            isLoading={
              createContract.isPending || isRecovering || !!pendingService
            }
            bundles={bundles}
            serviceItemTypes={serviceItemTypes}
            onSelectBundle={onSelectBundle}
          />
        )}
      </div>

      <AddServiceDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        selectedService={selectedService}
        contractId={localContractId}
        onAddToContract={handleAddToContract}
        currency={currency}
        awaitSave
      />
      {bundleDrawer}
    </>
  );
};

export default EmptyState;
